import React, { useMemo } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Button, Group, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconFileDownload, IconCloudUpload, IconRefresh } from '@tabler/icons-react';
import Tabla from '../../../globales/componentes/tabla/tabla';
import { obtenerColumnasVariacionMensual, type VariacionMensualTableMeta } from './constantesTablaVariacionMensual';
import type { IConfiguracionVariacionMensual } from '../interfaces';

interface TablaVariacionMensualVistaProps {
  datos: IConfiguracionVariacionMensual[];
  alDescargarPlantilla?: () => void;
  alCargaMasiva?: () => void;
  /** Vuelve a consultar la matriz (descarta una edición en curso). */
  alRecargar?: () => void;
  actualizarFila: (id: string, campo: keyof IConfiguracionVariacionMensual, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (id: string) => void;
  cancelarEdicion?: () => void;
  
  agregarFila: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
}

export const TablaVariacionMensualVista: React.FC<TablaVariacionMensualVistaProps> = ({
  datos,
  alDescargarPlantilla,
  alCargaMasiva,
  alRecargar,
  actualizarFila,
  eliminarFila,
  guardarFila,
  filaEnEdicion,
  iniciarEdicion,
  cancelarEdicion,
  agregarFila,
  mostrarNotificacionObligatorio,
  intentoGuardar
}) => {
  const columnas = useMemo(() => obtenerColumnasVariacionMensual(), []);

  const meta: VariacionMensualTableMeta = {
    actualizarFila,
    eliminarFila,
    guardarFila,
    filaEnEdicion,
    iniciarEdicion,
    cancelarEdicion,
    mostrarNotificacionObligatorio,
    intentoGuardar
  };

  const tabla = useReactTable({
    data: datos,
    columns: columnas,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.Id,
    meta
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm" c="dimmed">
          Variación Mensual de Flete
        </Text>
        <Group gap="sm">
          {/* Íconos con el estilo estándar de las tablas de los demás módulos (Tareas, Equipos, Clientes). */}
          <Group gap={4}>
            {alRecargar && (
              <Tooltip zIndex={100000} label="Refrescar" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
                <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alRecargar}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip zIndex={100000} label="Descargar Plantilla" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alDescargarPlantilla} disabled={!alDescargarPlantilla}>
                <IconFileDownload size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip zIndex={100000} label="Carga Masiva" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alCargaMasiva} disabled={!alCargaMasiva}>
                <IconCloudUpload size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Tooltip label="Ya se han agregado los 12 meses" disabled={datos.length < 12}>
            <div>
              <Button color="primary" variant="light" size="xs" radius="lg" leftSection={<IconPlus color="var(--mantine-color-primary-0)" size={12} />} onClick={agregarFila} disabled={(filaEnEdicion !== null && filaEnEdicion !== undefined) || datos.length >= 12}><Text fw={500} size="xs">Agregar Fila</Text></Button>
            </div>
          </Tooltip>
        </Group>
      </Group>

      <div style={{ flex: 1, minHeight: 300 }}>
        <Tabla tabla={tabla} modoEdicion={true} filaEnEdicion={filaEnEdicion} />
      </div>
    </div>
  );
};
